import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import AnalysisPage from "./pages/AnalysisPage";
import CustomPatternPage from "./pages/CustomPatternPage";
import DrawPatternPage from "./pages/DrawPatternPage";
import SavedAnalysesPage from "./pages/SavedAnalysesPage";
import PendingApprovalsPage from "./pages/PendingApprovalsPage";
import AdminProfilePage from "./pages/AdminProfilePage";
import SuperAdminUsersPage from "./pages/SuperAdminUsersPage";
import AdminBulletinPage from "./pages/AdminBulletinPage";
import PlatinumAnalysisPage from "./pages/PlatinumAnalysisPage";
import BISTAnalysisPage from "./pages/BISTAnalysisPage";
import HourlyAnalysisPage from "./pages/HourlyAnalysisPage";
import SignalScorecardPage from "./pages/SignalScorecardPage";
import SimonsScorePage from "./pages/SimonsScorePage";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
} from "./components/ui/alert-dialog";
import { Dialog, DialogContent } from "./components/ui/dialog";
import PackageModal from "./components/PackageModal";
import { Button } from "./components/ui/button";
import { toast } from "sonner";
import { ShieldAlert, TrendingUp, Zap, Crown } from "lucide-react";
import "./App.css";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F6F1EA] flex items-center justify-center">
        <div className="animate-pulse text-[#7A6A5C]">Yükleniyor...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F6F1EA] flex items-center justify-center">
        <div className="animate-pulse text-[#7A6A5C]">Yükleniyor...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const isAdmin = user?.role === "admin" || user?.role === "superadmin";
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const SuperAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F6F1EA] flex items-center justify-center">
        <div className="animate-pulse text-[#7A6A5C]">Yükleniyor...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const isSuperAdmin = user?.role === "superadmin";
  if (!isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route - Redirect to dashboard if logged in
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F6F1EA] flex items-center justify-center">
        <div className="animate-pulse text-[#7A6A5C]">Yükleniyor...</div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};


const PROMO_POPUP_KEY = "package_promo_v1_dismissed";

const PackagePromoPopup = () => {
  const { user, loading } = useAuth();
  const [open, setOpen] = useState(false);
  const [packageModalOpen, setPackageModalOpen] = useState(false);

  useEffect(() => {
    const isAdmin = user?.role === "admin" || user?.role === "superadmin";
    const isFree = !user?.package || user?.package === "free";
    if (!loading && user && isFree && !isAdmin && !localStorage.getItem(PROMO_POPUP_KEY)) {
      setOpen(true);
    }
  }, [user, loading]);

  const handleDismiss = () => {
    localStorage.setItem(PROMO_POPUP_KEY, "1");
    setOpen(false);
  };

  const handleUpgrade = () => {
    localStorage.setItem(PROMO_POPUP_KEY, "1");
    setOpen(false);
    setPackageModalOpen(true);
  };

  if (!user) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) handleDismiss(); }}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-md p-0 overflow-hidden border-0 rounded-2xl">
          {/* Üst başlık */}
          <div className="bg-[#2E2620] px-6 pt-7 pb-5 text-center">
            <div className="w-12 h-12 rounded-full bg-[#C86F4A]/20 flex items-center justify-center mx-auto mb-3">
              <TrendingUp className="w-6 h-6 text-[#C86F4A]" />
            </div>
            <h2 className="font-['Playfair_Display'] text-xl font-bold text-white">
              Paketinizi Yükseltin
            </h2>
            <p className="text-[#A89F91] text-xs mt-1.5">
              Şu an <span className="text-white font-semibold">Free</span> planındasınız — ayda yalnızca 1 sorgu hakkınız var.
            </p>
          </div>

          {/* Paket karşılaştırma */}
          <div className="bg-[#F6F1EA] px-5 pt-4 pb-2 space-y-2">
            <div className="flex items-center gap-3 bg-[#EDE9FE] border border-[#C4B5FD] rounded-xl px-4 py-3">
              <Zap className="w-4 h-4 text-[#5B21B6] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#2E2620]">Pro</p>
                <p className="text-xs text-[#7A6A5C]">Günde 3 sorgu hakkı</p>
              </div>
              <span className="text-sm font-bold text-[#5B21B6] flex-shrink-0">3.000 ₺<span className="text-xs font-normal text-[#7A6A5C]">/ay</span></span>
            </div>

            <div className="relative flex items-center gap-3 bg-[#CBD5E1]/40 border-2 border-[#8f969e] rounded-xl px-4 py-3">
              <span className="absolute -top-2.5 left-3 bg-[#C86F4A] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">Önerilen</span>
              <Crown className="w-4 h-4 text-[#3c3e42] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#2E2620]">Platinum</p>
                <p className="text-xs text-[#7A6A5C]">Sınırsız sorgu + Özel analizler</p>
              </div>
              <span className="text-sm font-bold text-[#2E2620] flex-shrink-0">5.000 ₺<span className="text-xs font-normal text-[#7A6A5C]">/ay</span></span>
            </div>
          </div>

          {/* Butonlar */}
          <div className="bg-[#F6F1EA] px-5 pb-5 pt-3 flex flex-col gap-2">
            <Button
              onClick={handleUpgrade}
              className="w-full bg-[#C86F4A] hover:bg-[#B05D3A] text-white rounded-full font-medium"
            >
              Paketleri İncele
            </Button>
            <button
              onClick={handleDismiss}
              className="text-xs text-[#7A6A5C] hover:text-[#2E2620] transition-colors py-1"
            >
              Şimdi değil
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <PackageModal
        open={packageModalOpen}
        onOpenChange={setPackageModalOpen}
        currentPkg={user?.package || "free"}
      />
    </>
  );
};

const LegalDisclaimerGate = () => {
  const { user, acceptDisclaimer } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const shouldShowDisclaimer = Boolean(user && !user.disclaimer_accepted);

  const handleAccept = async () => {
    setSubmitting(true);
    try {
      await acceptDisclaimer();
      toast.success("Yasal bilgilendirme onayınız kaydedildi.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Onay kaydedilemedi. Lütfen tekrar deneyin.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AlertDialog open={shouldShowDisclaimer}>
      <AlertDialogContent className="max-w-2xl border-[#E6DCCF] bg-[#F6F1EA]">
        <AlertDialogHeader className="space-y-3">
          <div className="mx-auto sm:mx-0 w-12 h-12 rounded-full bg-[#C86F4A]/10 flex items-center justify-center">
            <ShieldAlert className="w-6 h-6 text-[#C86F4A]" />
          </div>
          <AlertDialogTitle className="text-[#2E2620] font-['Playfair_Display'] text-2xl">
            Yasal Bilgilendirme ve Risk Uyarısı
          </AlertDialogTitle>
          <AlertDialogDescription className="text-[#7A6A5C] leading-relaxed text-sm">
            Bu platform benzer fiyat davranışlarını analiz ederek olası senaryolar üretir. Paylaşılan analizler, 
            grafikler, istatistikler ve yorumlar SPK mevzuatı kapsamında yatırım danışmanlığı hizmeti değildir. 
            Buradaki içerikler genel nitelikli bilgi ve kişisel değerlendirme niteliğindedir; her yatırımcının 
            risk-getiri tercihleri farklı olduğundan kararlarınız beklentilerinizle uyumlu sonuç vermeyebilir.
            Sermaye piyasalarında değer kaybı ve zarar riski bulunmaktadır. İşlem kararlarınızın tüm sorumluluğu size aittir.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction asChild>
            <Button
              onClick={handleAccept}
              disabled={submitting}
              className="bg-[#C86F4A] hover:bg-[#B05D3A] text-white rounded-full"
              data-testid="accept-disclaimer-btn"
            >
              {submitting ? "Kaydediliyor..." : "Okudum, Onaylıyorum"}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <LegalDisclaimerGate />
        <PackagePromoPopup />
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analysis"
            element={
              <ProtectedRoute>
                <AnalysisPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/custom-pattern"
            element={
              <AdminRoute>
                <CustomPatternPage />
              </AdminRoute>
            }
          />
          <Route
            path="/draw-pattern"
            element={
              <AdminRoute>
                <DrawPatternPage />
              </AdminRoute>
            }
          />
          <Route
            path="/saved"
            element={
              <ProtectedRoute>
                <SavedAnalysesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/pending-approvals"
            element={
              <AdminRoute>
                <PendingApprovalsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/profile"
            element={
              <AdminRoute>
                <AdminProfilePage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <SuperAdminRoute>
                <SuperAdminUsersPage />
              </SuperAdminRoute>
            }
          />
          <Route
            path="/admin/bulletin"
            element={
              <AdminRoute>
                <AdminBulletinPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/hourly-analysis"
            element={
              <AdminRoute>
                <HourlyAnalysisPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/signal-scorecard"
            element={
              <AdminRoute>
                <SignalScorecardPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/simons-score"
            element={
              <AdminRoute>
                <SimonsScorePage />
              </AdminRoute>
            }
          />
          <Route
            path="/simons-analizi"
            element={
              <ProtectedRoute>
                <PlatinumAnalysisPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bist-analizi"
            element={
              <ProtectedRoute>
                <BISTAnalysisPage />
              </ProtectedRoute>
            }
          />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
